import { useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { MenuItem } from '@headlessui/react';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  BellIcon,
  MegaphoneIcon,
} from '@heroicons/react/20/solid';
import { cn, Badge } from '@pantry/ui';
import type { NotificationResponse, Product } from '@pantry/shared';
import { getDateFnsLocale } from '@pantry/i18n';
import { formatDistanceToNow } from 'date-fns';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import type { ItemWithInstanceCounts } from '@pantry/api-client';

interface NotificationItemProps {
  notification: NotificationResponse;
  /** Pre-fetched item data for name resolution (from parent component) */
  item?: ItemWithInstanceCounts;
  onMarkAsRead: (id: string) => void;
  onClick?: () => void;
}

/**
 * Icon components mapped by notification type.
 */
const NOTIFICATION_ICONS: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  expiry_reminder: ClockIcon,
  low_stock_alert: ExclamationTriangleIcon,
  sharing_invitation: UserPlusIcon,
  system_announcement: MegaphoneIcon,
};

/**
 * Get the appropriate icon color for a notification type.
 */
function getIconColor(type: string) {
  switch (type) {
    case 'expiry_reminder':
      return 'text-warning';
    case 'low_stock_alert':
      return 'text-expired';
    case 'sharing_invitation':
      return 'text-primary';
    case 'system_announcement':
      return 'text-blue-500';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Get the appropriate icon background for a notification type.
 */
function getIconBackground(type: string) {
  switch (type) {
    case 'expiry_reminder':
      return 'bg-warning/10';
    case 'low_stock_alert':
      return 'bg-expired/10';
    case 'sharing_invitation':
      return 'bg-primary/10';
    case 'system_announcement':
      return 'bg-blue-50';
    default:
      return 'bg-muted';
  }
}

/**
 * Resolve item name from pre-fetched item or notification data.
 * Uses same pattern as inventory page: customName > resolveProductName > fallback
 */
function useResolvedItemName(
  item: ItemWithInstanceCounts | undefined,
  data: Record<string, unknown> | null
): string | null {
  const resolveProductName = useResolveProductName();
  const fallbackName = (data?.itemName as string) || null;

  return useMemo(() => {
    if (item) {
      // Use same pattern as inventory page: customName > resolveProductName > fallback
      return (
        item.customName ||
        (item.product ? resolveProductName(item.product as Product) : null) ||
        fallbackName
      );
    }
    // Item not available - use fallback from notification data
    return fallbackName;
  }, [item, resolveProductName, fallbackName]);
}

/**
 * Generate a display title for item-related notifications.
 * This ensures the resolved item name (including custom names) is used.
 */
function generateItemNotificationTitle(
  type: string,
  resolvedItemName: string,
  data: Record<string, unknown> | null,
  t?: ReturnType<typeof useLingui>['t']
): string {
  switch (type) {
    case 'expiry_reminder': {
      const expiresAt = data?.expiresAt as string | undefined;
      if (expiresAt) {
        const daysUntil = Math.ceil(
          (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        if (daysUntil <= 0) {
          return t ? t`${resolvedItemName} has expired` : `${resolvedItemName} has expired`;
        } else if (daysUntil === 1) {
          return t
            ? t`${resolvedItemName} expires tomorrow`
            : `${resolvedItemName} expires tomorrow`;
        } else {
          return t
            ? t`${resolvedItemName} expires in ${daysUntil} days`
            : `${resolvedItemName} expires in ${daysUntil} days`;
        }
      }
      return t ? t`${resolvedItemName} is expiring soon` : `${resolvedItemName} is expiring soon`;
    }
    case 'low_stock_alert':
      return t ? t`${resolvedItemName} is running low` : `${resolvedItemName} is running low`;
    default:
      return resolvedItemName;
  }
}

/**
 * Renders a single notification in the dropdown.
 */
export function NotificationItem({
  notification,
  item,
  onMarkAsRead,
  onClick,
}: NotificationItemProps) {
  const { t, i18n } = useLingui();
  const Icon = NOTIFICATION_ICONS[notification.type] ?? BellIcon;
  const iconColor = getIconColor(notification.type);
  const iconBg = getIconBackground(notification.type);

  // Resolve item name using pre-fetched item data (like inventory page)
  const resolvedItemName = useResolvedItemName(
    item,
    notification.data as Record<string, unknown> | null
  );

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    onClick?.();
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: getDateFnsLocale(i18n.locale),
  });

  // Get notification data for title/body generation
  const data = notification.data as Record<string, unknown> | null;
  const isItemNotification =
    notification.type === 'expiry_reminder' || notification.type === 'low_stock_alert';

  // Generate display title - use dynamic generation for item-related notifications
  const displayTitle = useMemo(() => {
    if (isItemNotification && resolvedItemName) {
      return generateItemNotificationTitle(notification.type, resolvedItemName, data, t);
    }
    return notification.title;
  }, [isItemNotification, notification.type, notification.title, resolvedItemName, data, t]);

  // Generate display body - for item notifications, generate dynamically
  const displayBody = useMemo(() => {
    if (!notification.body) return null;

    // For item notifications with resolved name, we can skip the body
    // since the title now contains all the relevant info
    if (isItemNotification && resolvedItemName) {
      return null;
    }

    return notification.body;
  }, [notification.body, isItemNotification, resolvedItemName]);

  return (
    <MenuItem>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors data-focus:bg-accent',
          !notification.isRead && 'bg-primary/5'
        )}
      >
        {/* Icon */}
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', iconBg)}>
          <Icon aria-hidden="true" className={cn('h-5 w-5', iconColor)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <p
                className={cn(
                  'text-sm leading-snug truncate',
                  notification.isRead ? 'text-muted-foreground' : 'text-foreground font-medium'
                )}
              >
                {displayTitle}
              </p>
              {/* Show "Expired" badge for expired announcements */}
              {notification.type === 'system_announcement' &&
                notification.announcementStatus === 'expired' && (
                  <Badge
                    variant="outline"
                    className="text-muted-foreground shrink-0 text-[10px] px-1.5 py-0"
                  >
                    <Trans>Expired</Trans>
                  </Badge>
                )}
            </div>
            {!notification.isRead && (
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
            )}
          </div>
          {displayBody && (
            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{displayBody}</p>
          )}
          <p className="mt-1 text-xs text-muted-foreground/70">{timeAgo}</p>
        </div>
      </button>
    </MenuItem>
  );
}
